/**
 * The Alipay top-up option on the billing page.
 *
 * The card checkout is priced in USD and can never show Alipay (a Japan-based
 * Stripe account presents Alipay only in JPY/CNY), so Alipay is a second
 * checkout of its own, charged in yen. See `src/lib/alipay-checkout.ts`.
 */

/** `GET /api/payment/config`, restated from `src/routes/payment-config.ts`;
 *  payment-config-contract.test.ts keeps the two in step. */
export interface PaymentConfig {
  publishableKey: string;
  /** USD cents. */
  minAmount: number;
  /** USD cents. */
  maxAmount: number;
  /** Null when the server has no recent rate: then there is no Alipay. */
  alipay: AlipayRate | null;
  chinaRegionIp: boolean;
}

export interface AlipayRate {
  jpyPerUsd: number;
  /** The ECB reference date, `YYYY-MM-DD`. */
  rateDate: string;
}

/** IANA zones observed in mainland China, Hong Kong, Macau and Taiwan,
 *  including the legacy aliases browsers still report. */
const CHINA_REGION_TIME_ZONES = new Set([
  'Asia/Shanghai',
  'Asia/Chongqing',
  'Asia/Chungking',
  'Asia/Harbin',
  'Asia/Urumqi',
  'Asia/Kashgar',
  'Asia/Hong_Kong',
  'Asia/Macau',
  'Asia/Macao',
  'Asia/Taipei',
  'PRC',
  'ROC',
  'Hongkong',
]);

/**
 * Whether to show the Alipay button up front rather than behind "Other
 * payment methods". Any one signal is enough: the UI language misses Chinese
 * users who browse in English, the IP misses those on a VPN, and the time zone
 * — the one thing a VPN does not change — misses travellers.
 */
export function showsAlipayUpFront(signals: {
  uiLocale: string;
  browserLanguages: readonly string[];
  timeZone: string | undefined;
  chinaRegionIp: boolean;
}): boolean {
  const isChinese = (tag: string) => tag.toLowerCase().startsWith('zh');
  return (
    signals.chinaRegionIp ||
    isChinese(signals.uiLocale) ||
    signals.browserLanguages.some(isChinese) ||
    (signals.timeZone !== undefined && CHINA_REGION_TIME_ZONES.has(signals.timeZone))
  );
}

/**
 * The whole yen Stripe charges for an Alipay top-up of `usd`: the same
 * arithmetic as the server's `jpyForUsdCents`, pinned by the contract test.
 * The payer's Alipay app shows them yuan at its own rate.
 */
export function alipayYen(usd: number, rate: AlipayRate): number {
  return Math.round((Math.round(usd * 100) * rate.jpyPerUsd) / 100);
}
